import { FC, ReactNode } from 'react';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import {
  BODY_TYPES,
  BodyTypeMessageId,
} from '@core/Utils/VehicleMappedMessages/BodyTypeMappings.constants';
import { useAtomValue } from 'jotai';

import { MultiSelect } from 'Components/MultiSelect/MultiSelect';
import { useFilterLink } from 'Hooks/SearchFilters/UseFilterLink';
import {
  MultiSelectOption,
  useMultiSelectFilters,
} from 'Hooks/SearchFilters/UseMultiSelectFilters';
import { getOptionsWithAggs } from 'Utils/SearchFilters/GetOptionsWithAggs';

import { FilterControlBaseProps, PlpFiltersMessagesKeys } from '../../FilterPanel.entity';

import { FilterLink } from '../FilterLink/FilterLink';

import { ICON_MAPPER } from './BodyTypeFilter.constants';

import styles from './BodyTypeFilter.module.css';

type BodyTypeFilterProps = FilterControlBaseProps;
const filterId: FilterKey = 'body-type';

const BodyTypeFilter: FC<BodyTypeFilterProps> = ({
  strings,
  locale,
  isPlpFiltersSeoEnabled,
  tenant,
}) => {
  const aggs = useAtomValue(aggregationsAtom);
  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    locale,
    isPlpFiltersSeoEnabled,
    tenant,
  });
  const listEnhanceFn = (bodyType: MultiSelectOption, selected: string[]) => {
    const link = getLinkForFilterValue({
      filterValue: bodyType.id,
      resultCount: bodyType.count,
    });
    return {
      ...bodyType,
      renderLabel: renderLabel(
        bodyType.labelKey as BodyTypeMessageId,
        <FilterLink link={link}>{strings[bodyType.labelKey as PlpFiltersMessagesKeys]}</FilterLink>,
      ),
      selected: selected?.includes(bodyType.id),
    };
  };

  const { handleSelect, listWithSelected } = useMultiSelectFilters(
    filterId,
    getOptionsWithAggs(BODY_TYPES, aggs['body-type']),
    strings,
    locale,
    isPlpFiltersSeoEnabled,
    tenant,
    listEnhanceFn,
  );

  return (
    <section className={styles.bodyTypeFilter}>
      <MultiSelect
        groupName="body-type"
        classes={{ label: styles.listItem }}
        items={listWithSelected}
        onSelect={handleSelect}
        dataTestId="body-type-filter-list"
      />
    </section>
  );
};

const renderLabel = (name: BodyTypeMessageId, label: ReactNode) => {
  const Component = ICON_MAPPER[name];
  return Component ? (
    <>
      <Component
        fontSize={40}
        className={name === 'motorhome' || name === 'sportscar' ? '' : styles.icon}
      />{' '}
      {label}
    </>
  ) : null;
};

export default BodyTypeFilter;
